import {
  ALL,
  DISTRIBUTION,
  LEGACY_ASSIGNMENT_REPORT_PERIODS,
  PAGE_SIZE,
  RANGE_DATE_TO_PERIOD,
  SCOPE,
} from '@/components/Webcontact/assignmentReport/constants';
import {
  getRangeDateBounds,
  RANGE_DATE_PRESET,
} from '@/components/FilterBar/constants/rangeDateOptions';

const SORT_BY = {
  contact: 'contact',
  property: 'property',
  operation: 'operation',
  origin: 'origin',
  income: 'income',
  agent: 'agent',
  responseTime: 'response_time',
  result: 'result',
  assignment: 'assignment',
};

// An empty list means "all": the param is omitted.
function appendList(params, key, values) {
  if (!values?.length) return;
  values.forEach((value) => params.append(key, value));
}

/**
 * New backends prefer date_from/date_to (they win over period when both are
 * present). Older backends ignore dates and only understand
 * period=last_7|30|90_days. Newer presets only send dates so an old API does
 * not 400 — it degrades to the default 30-day window instead.
 */
function appendDateRange(params, dateRange) {
  if (!dateRange) return;

  const { date_from, date_to } = getRangeDateBounds(dateRange);
  if (date_from) params.set('date_from', date_from);
  if (date_to) params.set('date_to', date_to);

  if (dateRange.id === RANGE_DATE_PRESET.CUSTOM) return;

  const period = RANGE_DATE_TO_PERIOD[dateRange.id];
  if (period && LEGACY_ASSIGNMENT_REPORT_PERIODS.has(period)) {
    params.set('period', period);
  }
}

export function summarySearchParams(filters) {
  const params = new URLSearchParams();
  appendList(params, 'origin_keys', filters?.originKeys);
  if (filters?.scope === SCOPE.TEAMS) {
    appendList(params, 'team_ids', filters.scopeIds);
  } else if (filters?.scope === SCOPE.AGENTS) {
    appendList(params, 'agent_ids', filters.scopeIds);
  } else {
    appendList(params, 'branch_ids', filters?.scopeIds);
  }
  appendDateRange(params, filters?.dateRange);
  return params;
}

export function detailSearchParams(query, { includePage = true } = {}) {
  const params = summarySearchParams(query);
  if (query?.distributionType) {
    params.set('distribution_type', query.distributionType);
  }
  if (query?.distributionType === DISTRIBUTION.RULE && query.ruleId != null) {
    params.set('rule_id', query.ruleId);
  }
  const assigned =
    query?.distributionType === DISTRIBUTION.MANUAL ||
    query?.distributionType === DISTRIBUTION.RULE;
  if (assigned && query?.agentId != null) {
    params.set('agent_id', query.agentId);
  }
  const search = (query?.search || '').trim();
  if (search) {
    params.set('search', search);
    if (query.searchField) params.set('search_field', query.searchField);
  }
  if (query?.operation && query.operation !== ALL) {
    params.set('operation', query.operation);
  }
  if (assigned && query?.result && query.result !== ALL) {
    params.set('result', query.result);
  }
  if (query?.sortBy) {
    params.set('sort_by', SORT_BY[query.sortBy] || query.sortBy);
  }
  if (query?.sortDirection) params.set('sort_direction', query.sortDirection);
  if (includePage) {
    params.set('page', query?.page || 1);
    params.set('page_size', query?.pageSize || PAGE_SIZE);
  }
  return params;
}

function mapResults(results) {
  if (!results) return null;
  return {
    unattended: results.unattended,
    lost: results.lost,
    inProgress: results.in_progress,
    won: results.won,
  };
}

function mapMetrics(row) {
  return {
    count: row.count,
    percent: row.percent,
    results: mapResults(row.results),
    medianResponseMinutes: row.median_response_minutes,
    conversionRate: row.conversion_rate,
    unattended: row.unattended_count,
  };
}

export function mapSummary(data) {
  return {
    total: data?.total ?? 0,
    rows: (data?.rows || []).map((row) => ({
      id: row.id,
      type: row.type,
      name: row.name,
      criteriaSummary: row.criteria_summary || '',
      ...mapMetrics(row),
      agents: row.agents
        ? row.agents.map((agent) => ({
            id: agent.id,
            name: agent.name,
            branchName: agent.branch_name,
            ...mapMetrics(agent),
          }))
        : undefined,
    })),
  };
}

export function mapInquiry(row) {
  return {
    id: row.id,
    distributionType: row.distribution_type,
    ruleId: row.rule_id,
    agentId: row.agent_id,
    contactId: row.contact_id,
    contactName: row.contact_name,
    contactCellphone: row.contact_cellphone,
    contactPhone: row.contact_phone,
    // Keep null/undefined when the API omits the flag so ContactCell can
    // fall back to "any cellphone means WhatsApp" for older payloads.
    isValidCellphone:
      row.is_valid_cellphone == null ? null : Boolean(row.is_valid_cellphone),
    contactEmail: row.contact_email,
    propertyId: row.property_id,
    propertyName: row.property_name,
    propertyType: row.property_type,
    operation: row.operation,
    valueLabel: row.value_label,
    origin: row.origin_label,
    incomeDate: row.income_date,
    agentName: row.agent_name,
    agentBranch: row.agent_branch,
    responseTimeMinutes: row.response_time_minutes,
    result: row.result,
    assignmentDate: row.assignment_date,
    closeDate: row.close_date,
    closureReason: row.closure_reason,
    closureQualification: row.closure_qualification,
  };
}

export function mapDetailPage(data) {
  return {
    count: data?.count ?? 0,
    page: data?.page ?? 1,
    pageSize: data?.page_size ?? PAGE_SIZE,
    rows: (data?.results || []).map(mapInquiry),
  };
}
